/**
 * Email Content Parser Service
 * Intelligently extracts structured data from customer email content
 * to auto-populate customer case creation forms
 */

// Interface matching CustomerCaseCreationForm fields
interface ParsedEmailContent {
  customerInfo: {
    customer_company?: string;
    customer_name?: string;
    customer_email?: string;
    phone?: string;
    address?: string;
  };
  caseInfo: {
    title?: string;
    description?: string;
    priority?: 1 | 2 | 3 | 4 | 5;
    quote_deadline?: string;
  };
  positions: {
    part_name: string;
    material?: string;
    quantity?: number;
    specifications?: string;
    position_number: number;
  }[];
  extractionMetadata: {
    confidence: 'high' | 'medium' | 'low';
    extractedFields: string[];
    detectedLanguage: 'de' | 'en' | 'unknown';
    positionCount: number;
  };
}

class EmailContentParser {
  
  /**
   * Parse customer email content and extract structured data
   */
  static parseCustomerRequest(emailContent: {
    from_address: string;
    subject: string;
    body_text?: string;
    body_html?: string;
  }): ParsedEmailContent {
    
    const content = emailContent.body_text || emailContent.body_html || '';
    const subject = emailContent.subject || '';
    
    // Initialize result structure
    const result: ParsedEmailContent = {
      customerInfo: {
        customer_email: emailContent.from_address
      },
      caseInfo: {
        title: this.generateCaseTitle(subject),
        priority: 3 // Default medium priority
      },
      positions: [],
      extractionMetadata: {
        confidence: 'low',
        extractedFields: ['customer_email'],
        detectedLanguage: this.detectLanguage(content),
        positionCount: 0
      }
    };

    // Extract customer information
    result.customerInfo = {
      ...result.customerInfo,
      ...this.extractCustomerInfo(content, emailContent.from_address)
    };

    // Extract project/case information
    result.caseInfo = {
      ...result.caseInfo,
      ...this.extractCaseInfo(content, subject)
    };

    // Extract positions (most important!)
    result.positions = this.extractPositions(content);
    result.extractionMetadata.positionCount = result.positions.length;

    // Calculate confidence and extracted fields
    result.extractionMetadata = this.calculateExtractionMetadata(result);

    return result;
  }

  /**
   * Generate a meaningful case title from email subject
   */
  private static generateCaseTitle(subject: string): string {
    // Remove common email prefixes
    let title = subject
      .replace(/^(RE:|FW:|AW:|Antwort:|Weiterleitung:)/i, '')
      .trim();
    
    // If title is still generic or empty, provide a default
    if (!title || title.length < 10) {
      title = `Customer Quote Request - ${new Date().toLocaleDateString('de-DE')}`;
    }
    
    return title;
  }

  /**
   * Detect email language (German/English)
   */
  private static detectLanguage(content: string): 'de' | 'en' | 'unknown' {
    const germanKeywords = ['angebot', 'anfrage', 'bitte', 'benötigen', 'stück', 'lieferung', 'material'];
    const englishKeywords = ['quote', 'request', 'please', 'need', 'pieces', 'delivery', 'material'];
    
    const lowerContent = content.toLowerCase();
    const germanScore = germanKeywords.reduce((score, word) => 
      score + (lowerContent.includes(word) ? 1 : 0), 0);
    const englishScore = englishKeywords.reduce((score, word) => 
      score + (lowerContent.includes(word) ? 1 : 0), 0);
    
    if (germanScore > englishScore) return 'de';
    if (englishScore > germanScore) return 'en';
    return 'unknown';
  }

  /**
   * Extract customer information from email content
   */
  private static extractCustomerInfo(content: string, fromEmail: string): Partial<ParsedEmailContent['customerInfo']> {
    const info: Partial<ParsedEmailContent['customerInfo']> = {};

    // Extract company name patterns
    const companyPatterns = [
      /(?:^|\n)([A-ZÄÖÜ][a-zA-ZäöüÄÖÜß\s]+(?:GmbH|AG|KG|OHG|e\.V\.|Inc\.|LLC|Ltd\.|Corp\.?))/gm,
      /Mit freundlichen Grüßen[\s\n]*([A-ZÄÖÜ][a-zA-ZäöüÄÖÜß\s]+(?:GmbH|AG|KG))/i,
      /([A-ZÄÖÜ][a-zA-ZäöüÄÖÜß\s]+(?:GmbH|AG|KG|OHG|e\.V\.)) from email/i
    ];

    for (const pattern of companyPatterns) {
      const match = content.match(pattern);
      if (match && match[1]) {
        info.customer_company = match[1].trim();
        break;
      }
    }

    // Extract contact person name
    const namePatterns = [
      /Mit freundlichen Grüßen[\s\n]*([A-ZÄÖÜ][a-zA-ZäöüÄÖÜß]+\s+[A-ZÄÖÜ][a-zA-ZäöüÄÖÜß]+)/i,
      /Best regards[\s\n]*([A-Z][a-zA-Z]+\s+[A-Z][a-zA-Z]+)/i,
      /([A-ZÄÖÜ][a-zA-ZäöüÄÖÜß]+\s+[A-ZÄÖÜ][a-zA-ZäöüÄÖÜß]+)[\s\n]*(?:Einkaufsleiter|Procurement|Manager)/i
    ];

    for (const pattern of namePatterns) {
      const match = content.match(pattern);
      if (match && match[1]) {
        info.customer_name = match[1].trim();
        break;
      }
    }

    // Extract phone number
    const phonePattern = /(?:Tel|Phone|Telefon)[:\s]*(\+?\d+[\s\-\d]+)/i;
    const phoneMatch = content.match(phonePattern);
    if (phoneMatch) {
      info.phone = phoneMatch[1].trim();
    }

    return info;
  }

  /**
   * Extract case/project information
   */
  private static extractCaseInfo(content: string, subject: string): Partial<ParsedEmailContent['caseInfo']> {
    const info: Partial<ParsedEmailContent['caseInfo']> = {};

    // Extract delivery deadline
    const deadlinePatterns = [
      /(?:Liefertermin|Delivery|bis|until|spätestens|latest)[\s:]*(\d{1,2}\.?\s*\d{1,2}\.?\s*\d{4})/i,
      /(\d{1,2}\.?\s*\d{1,2}\.?\s*\d{4})[\s\n]*(?:gebraucht|needed|required)/i
    ];

    for (const pattern of deadlinePatterns) {
      const match = content.match(pattern);
      if (match && match[1]) {
        // Convert to ISO date format
        const dateStr = match[1].replace(/\s/g, '');
        try {
          const [day, month, year] = dateStr.split('.');
          const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          info.quote_deadline = date.toISOString().split('T')[0];
        } catch (e) {
          // Invalid date format, skip
        }
        break;
      }
    }

    // Extract urgency level for priority
    const urgencyPatterns = [
      { pattern: /(?:urgent|dringend|asap|sofort)/i, priority: 5 as const },
      { pattern: /(?:schnell|quick|priorität|priority)/i, priority: 4 as const },
      { pattern: /(?:standard|normal|regular)/i, priority: 3 as const }
    ];

    for (const { pattern, priority } of urgencyPatterns) {
      if (content.match(pattern)) {
        info.priority = priority;
        break;
      }
    }

    // Generate description from email content (first 200 chars)
    const cleanContent = content
      .replace(/Mit freundlichen Grüßen[\s\S]*$/i, '') // Remove signature
      .replace(/Best regards[\s\S]*$/i, '')
      .replace(/\n{2,}/g, '\n\n') // Normalize line breaks
      .trim();
    
    if (cleanContent.length > 50) {
      info.description = cleanContent.substring(0, 200) + (cleanContent.length > 200 ? '...' : '');
    }

    return info;
  }

  /**
   * Extract positions from email content (MOST IMPORTANT FUNCTION)
   */
  private static extractPositions(content: string): ParsedEmailContent['positions'] {
    const positions: ParsedEmailContent['positions'] = [];

    // German position patterns
    const germanPatterns = [
      /Position\s*(\d+)[:\s]*([^:]+?)[\s\n]*(?:Material|Werkstoff)[:\s]*([^:]+?)[\s\n]*(?:Menge|Anzahl)[:\s]*(\d+)[\s]*(?:Stück|pieces?|pcs?)?/gi,
      /(\d+)\.\s*([^:]+?)[\s\n]*(?:Material|Werkstoff)[:\s]*([^:]+?)[\s\n]*(?:Menge|Anzahl)[:\s]*(\d+)/gi,
      /(?:Pos\.?\s*(\d+)|Position\s*(\d+))[:\s-]*([^,\n]+?)(?:,|\n)[\s\S]*?(?:Menge|Anzahl|Qty)[:\s]*(\d+)/gi
    ];

    // English position patterns  
    const englishPatterns = [
      /Position\s*(\d+)[:\s]*([^:]+?)[\s\n]*(?:Material)[:\s]*([^:]+?)[\s\n]*(?:Quantity|Qty)[:\s]*(\d+)[\s]*(?:pieces?|pcs?)?/gi,
      /(\d+)\.\s*([^:]+?)[\s\n]*(?:Material)[:\s]*([^:]+?)[\s\n]*(?:Quantity|Qty)[:\s]*(\d+)/gi
    ];

    // Try German patterns first
    let positionNumber = 1;
    for (const pattern of germanPatterns) {
      let match;
      while ((match = pattern.exec(content)) !== null) {
        const posNum = parseInt(match[1] || match[2]) || positionNumber;
        const partName = (match[2] || match[3] || '').trim();
        const material = (match[3] || match[4] || '').trim();
        const quantity = parseInt(match[4] || match[5] || '0');

        if (partName && quantity > 0) {
          positions.push({
            position_number: posNum,
            part_name: partName,
            material: material,
            quantity: quantity,
            specifications: this.extractSpecifications(content, partName)
          });
          positionNumber++;
        }
      }
    }

    // If no positions found with German patterns, try English
    if (positions.length === 0) {
      for (const pattern of englishPatterns) {
        let match;
        while ((match = pattern.exec(content)) !== null) {
          const posNum = parseInt(match[1]) || positionNumber;
          const partName = (match[2] || '').trim();
          const material = (match[3] || '').trim();
          const quantity = parseInt(match[4] || '0');

          if (partName && quantity > 0) {
            positions.push({
              position_number: posNum,
              part_name: partName,
              material: material,
              quantity: quantity,
              specifications: this.extractSpecifications(content, partName)
            });
            positionNumber++;
          }
        }
      }
    }

    // Fallback: Look for simple item lists
    if (positions.length === 0) {
      const simplePatterns = [
        /(?:^|\n)[-*•]\s*([^:\n]+?)[\s\n]*(?:Menge|Anzahl|Quantity)[:\s]*(\d+)/gim,
        /(\d+)[\s]*(?:Stück|pieces?|pcs?|x)[\s]*([^,\n]+)/gi
      ];

      for (const pattern of simplePatterns) {
        let match;
        while ((match = pattern.exec(content)) !== null && positions.length < 5) {
          const quantity = parseInt(match[2] || match[1]);
          const partName = (match[1] || match[2] || '').trim();

          if (partName && quantity > 0) {
            positions.push({
              position_number: positionNumber++,
              part_name: partName,
              quantity: quantity,
              specifications: this.extractSpecifications(content, partName)
            });
          }
        }
      }
    }

    return positions.slice(0, 10); // Limit to 10 positions max
  }

  /**
   * Extract additional specifications for a part
   */
  private static extractSpecifications(content: string, partName: string): string {
    const specs: string[] = [];
    
    // Look for specifications near the part name
    const partIndex = content.toLowerCase().indexOf(partName.toLowerCase());
    if (partIndex === -1) return '';
    
    // Extract context around the part name (±200 characters)
    const contextStart = Math.max(0, partIndex - 200);
    const contextEnd = Math.min(content.length, partIndex + partName.length + 200);
    const context = content.substring(contextStart, contextEnd);
    
    // Common specification patterns
    const specPatterns = [
      /(?:Durchmesser|Diameter)[:\s]*(\d+(?:\.\d+)?)\s*mm/i,
      /(?:Länge|Length)[:\s]*(\d+(?:\.\d+)?)\s*mm/i,
      /(?:Beschichtung|Coating)[:\s]*([A-Za-z0-9]+)/i,
      /(?:Toleranz|Tolerance)[:\s]*([±\d.,]+)/i,
      /(?:Oberfläche|Surface)[:\s]*([^,\n]+)/i
    ];
    
    for (const pattern of specPatterns) {
      const match = context.match(pattern);
      if (match && match[1]) {
        specs.push(match[0].trim());
      }
    }
    
    return specs.join('; ');
  }

  /**
   * Calculate extraction metadata (confidence, etc.)
   */
  private static calculateExtractionMetadata(result: ParsedEmailContent): ParsedEmailContent['extractionMetadata'] {
    const extractedFields: string[] = ['customer_email']; // Always have email
    let confidence: 'high' | 'medium' | 'low' = 'low';

    // Count extracted fields
    if (result.customerInfo.customer_company) extractedFields.push('customer_company');
    if (result.customerInfo.customer_name) extractedFields.push('customer_name');
    if (result.caseInfo.quote_deadline) extractedFields.push('quote_deadline');
    if (result.positions.length > 0) extractedFields.push('positions');
    if (result.caseInfo.description) extractedFields.push('description');

    // Calculate confidence based on extracted data quality
    const hasPositions = result.positions.length > 0;
    const hasQuantities = result.positions.some(p => (p.quantity || 0) > 0);
    const hasCompany = !!result.customerInfo.customer_company;

    if (hasPositions && hasQuantities && hasCompany && extractedFields.length >= 5) {
      confidence = 'high';
    } else if (hasPositions && (hasCompany || extractedFields.length >= 3)) {
      confidence = 'medium';
    }

    return {
      confidence,
      extractedFields,
      detectedLanguage: result.extractionMetadata.detectedLanguage,
      positionCount: result.positions.length
    };
  }

  /**
   * Convert parsed data to CustomerCaseFormData format
   */
  static convertToFormData(
    parsedData: ParsedEmailContent, 
    emailContext: any
  ): Partial<any> {
    const positions = parsedData.positions.map((pos, index) => ({
      position_id: `POS-${String(index + 1).padStart(3, '0')}`,
      position_number: pos.position_number || index + 1,
      part_name: pos.part_name,
      material: pos.material || '',
      quantity: pos.quantity || 0,
      specifications: pos.specifications || ''
    }));

    // If no positions were extracted, provide one empty position
    if (positions.length === 0) {
      positions.push({
        position_id: 'POS-001',
        position_number: 1,
        part_name: '',
        material: '',
        quantity: 0,
        specifications: ''
      });
    }

    return {
      customer_email: parsedData.customerInfo.customer_email || emailContext.from_address,
      customer_name: parsedData.customerInfo.customer_name || '',
      customer_company: parsedData.customerInfo.customer_company || '',
      title: parsedData.caseInfo.title || `Quote Request - ${new Date().toLocaleDateString('de-DE')}`,
      description: parsedData.caseInfo.description || '',
      priority: parsedData.caseInfo.priority || 3,
      quote_deadline: parsedData.caseInfo.quote_deadline || '',
      positions: positions,
      related_email_id: emailContext.email_id
    };
  }
}

export { EmailContentParser, type ParsedEmailContent };
